import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { type PortalSettings, type KeyFiguresFieldValue, type KeyFigureFunction } from '@gorilla/portal-shared/src/schemas';
import { type KeyFiguresFieldDescriptor } from '@gorilla/portal-shared/src/types';
import {
  coreSectionDescriptorsByType,
  hasKeyFigureSource,
  KEY_FIGURE_SOURCE_SECTION_TYPES,
} from '@gorilla/portal-shared/src/core-sections';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton, TextField, Box, Flex, Text, Icon, Dropdown, Button, Heading } from '@vibe/core';
import { Delete, AddSmall, CloseSmall, Link, Drag, Alert, Hide, File, Settings } from '@vibe/icons';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { ColumnPicker } from '../../shared/components/ColumnPicker';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';
import { PagePickerModal } from '../PagePickerModal';
import { getSwatchThemeStyle, SwatchSetting } from '../SwatchSetting';
import { createRandomId } from '../../lib/utils';

const isExternalUrl = (url?: string) => /^(https?:\/\/|mailto:|tel:)/i.test((url ?? '').trim());
const getTextFieldValue = (evOrValue: any) => (typeof evOrValue === 'string' ? evOrValue : (evOrValue?.target?.value ?? ''));

// one grid for the header and every row, so the columns line up whatever the cells contain
const ROW_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '18px 72px minmax(50px, 1fr) minmax(50px, 1fr) minmax(50px, 1fr) 64px 40px',
  columnGap: '14px',
  alignItems: 'end',
  width: '100%',
};

const makeRowId = () => `figure-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Font Awesome icon names, rendered as `fa-regular fa-<value>` like the callout icons
const ICON_OPTIONS = [
  { value: '', label: 'No icon' },
  { value: 'sack-dollar', label: 'Money' },
  { value: 'euro-sign', label: 'Euro' },
  { value: 'dollar-sign', label: 'Dollar' },
  { value: 'file-invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'clock', label: 'Time' },
  { value: 'hourglass-half', label: 'Hourglass' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'list-check', label: 'Tasks' },
  { value: 'circle-check', label: 'Done' },
  { value: 'bars-progress', label: 'Progress' },
  { value: 'chart-line', label: 'Trend' },
  { value: 'chart-pie-simple', label: 'Share' },
  { value: 'percent', label: 'Percent' },
  { value: 'box', label: 'Box' },
  { value: 'truck-fast', label: 'Delivery' },
  { value: 'users', label: 'People' },
  { value: 'star', label: 'Star' },
  { value: 'flag', label: 'Flag' },
  { value: 'bolt', label: 'Bolt' },
];

// "No icon" shows a muted ⊘. The box is as tall as a line of text, which is what the dropdown's value area is padded
// for, so a lone icon sits in the middle.
const IconOptionIcon = ({ value }: { value: string }) => (
  <Flex align="center" justify="center" style={{ width: '16px', height: '22px', flexShrink: 0 }}>
    <i className={`fa-regular fa-${value || 'ban'}`} style={value ? undefined : { color: 'var(--secondary-text-color)', opacity: 0.6 }}></i>
  </Flex>
);

// the closed dropdown shows just the icon, so the column stays narrow
const renderIconValue = ({ value }: { value: string }) => <IconOptionIcon value={value} />;

const renderIconOption = ({ value, label }: { value: string; label: string }) => (
  <Flex gap={8} align="center">
    <IconOptionIcon value={value} />
    <Box style={value ? undefined : { color: 'var(--secondary-text-color)' }}>{label}</Box>
  </Flex>
);

const FUNCTION_OPTIONS: { value: KeyFigureFunction; label: string; endElement: { type: 'suffix'; value: string } }[] = [
  { value: 'sum', label: 'Sum', endElement: { type: 'suffix', value: 'SUM' } },
  { value: 'avg', label: 'Average', endElement: { type: 'suffix', value: 'AVG' } },
  { value: 'median', label: 'Median', endElement: { type: 'suffix', value: 'MED' } },
  { value: 'min', label: 'Minimum', endElement: { type: 'suffix', value: 'MIN' } },
  { value: 'max', label: 'Maximum', endElement: { type: 'suffix', value: 'MAX' } },
  { value: 'count', label: 'Count values', endElement: { type: 'suffix', value: 'COUNT' } },
  { value: 'count_items', label: 'Count items', endElement: { type: 'suffix', value: 'COUNT' } },
];

type KeyFigure = NonNullable<KeyFiguresFieldValue>[number];
type KeyFigureSourceSetup = Pick<KeyFigure, 'sectionId' | 'columnId' | 'function'>;

type KeyFigureSource = {
  id: string;
  label: string;
  pageName: string;
  hidden: boolean;
  section: PortalSettings['pages'][number]['sections'][number];
};

// The sections a key figure can be computed over, on every page, labelled "Page → Section (Headline)".
function getKeyFigureSources(portalSettings: PortalSettings): KeyFigureSource[] {
  return portalSettings.pages.flatMap((page) =>
    page.sections
      .filter((section) => KEY_FIGURE_SOURCE_SECTION_TYPES.includes(section.type))
      .map((section) => {
        const typeTitle = coreSectionDescriptorsByType[section.type]?.title ?? section.type;
        const headline = section.fieldValues?.headline?.headline;

        return {
          id: section.id,
          label: `${page.name} → ${typeTitle}${headline ? ` (${headline})` : ''}`,
          pageName: page.name,
          hidden: !!(page.hidden || section.hidden),
          section,
        };
      }),
  );
}

// The numbers columns of a source section's board (the aggregate API only sums real numbers columns, not
// mirror or formula columns), plus what is wrong with a setup, if anything.
function useKeyFigureSource(
  setup: KeyFigureSourceSetup,
  sources: KeyFigureSource[],
  portalSettings: PortalSettings,
  clientBoardSubitemsBoardId: number | null,
) {
  const source = sources.find((candidate) => candidate.id === setup.sectionId);

  const { board, isLoading } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor: coreSectionDescriptorsByType[source?.section.type ?? 'item-table']!,
    clientBoardSubitemsBoardId,
    sectionFieldValues: source?.section.fieldValues ?? {},
  });

  const numbersColumns = (board?.columns || []).filter((column) => column.type === 'numbers');
  const column = numbersColumns.find((candidate) => candidate.id === setup.columnId);

  let warning: string | null = null;

  if (setup.sectionId && !source) {
    warning = 'The section this figure comes from was deleted.';
  } else if (source?.hidden) {
    warning =
      'The selected section above is hidden, so clients won’t be able to see this figure. Make the section visible, or use a section condition instead.';
  } else if (setup.columnId && setup.function !== 'count_items' && board && !isLoading && !column) {
    warning = 'The column is no longer a numbers column of this board.';
  }

  return { source, board, isLoading, numbersColumns, column, warning };
}

type SourceModalProps = {
  figure: KeyFigure;
  sources: KeyFigureSource[];
  portalSettings: PortalSettings;
  clientBoardSubitemsBoardId: number | null;
  onSave: (setup: KeyFigureSourceSetup) => void;
  onClose: () => void;
};

function KeyFigureSourceModal({ figure, sources, portalSettings, clientBoardSubitemsBoardId, onSave, onClose }: SourceModalProps) {
  const [draft, setDraft] = useState<KeyFigureSourceSetup>(() => ({
    sectionId: figure.sectionId ?? sources[0]?.id,
    columnId: figure.columnId,
    function: figure.function ?? 'sum',
  }));
  const { source, board, isLoading, numbersColumns, warning } = useKeyFigureSource(
    draft,
    sources,
    portalSettings,
    clientBoardSubitemsBoardId,
  );
  const isCountItems = draft.function === 'count_items';

  // a single numbers column is the obvious choice
  useEffect(() => {
    if (!isCountItems && !draft.columnId && numbersColumns.length === 1) {
      setDraft((current) => ({ ...current, columnId: numbersColumns[0]!.id }));
    }
  }, [isCountItems, draft.columnId, numbersColumns]);

  const sectionOptions = sources.map((candidate) => ({
    value: candidate.id,
    label: candidate.label,
    startElement: {
      type: 'icon',
      value: File,
    },
    endElement: {
      type: 'icon',
      value: candidate.hidden ? Hide : undefined,
    },
  }));
  const selectedSectionOption = sectionOptions.find((option) => option.value === draft.sectionId) || null;
  const selectedFunctionOption = FUNCTION_OPTIONS.find((option) => option.value === draft.function) || null;
  const isComplete = hasKeyFigureSource({ ...draft, id: 'draft' }) && !!source;

  return (
    <AppModal show onClose={onClose} width={460}>
      <AppModalContent style={{ overflow: 'visible' }}>
        <Flex direction="row" align="center" gap={8} style={{ marginBottom: '8px' }}>
          <Icon icon={Settings} size={22} />
          <Heading type="h3" weight="bold" align="start">
            Number setup
          </Heading>
        </Flex>
        <Text type="text2" color="secondary" ellipsis={false} element="p" style={{ marginBottom: '20px' }}>
          Shows a column total or count from an existing Board Table or Grid section, over the same items that the section shows to the
          client, with its filters applied.
        </Text>

        <Flex direction="column" align="stretch" gap={16}>
          <Flex direction="column" align="start" gap={6} style={{ position: 'relative' }}>
            <Text type="text2">Section</Text>
            <Text type="text3" color="secondary" style={{ position: 'absolute', top: '4px', right: '0' }}>
              Page → Section (Headline)
            </Text>
            <div style={{ position: 'relative', width: '100%' }}>
              <Dropdown
                size="medium"
                placeholder={sources.length ? 'Choose a Board Table or Grid section' : 'Add a Board Table or Grid section first'}
                clearable={false}
                searchable={false}
                disabled={!sources.length}
                value={dropdownValue(selectedSectionOption)}
                options={sectionOptions}
                onChange={(option: any) => {
                  if (option && option.value !== draft.sectionId) {
                    // column ids belong to the previous section's board
                    setDraft({ ...draft, sectionId: option.value, columnId: undefined });
                  }
                }}
              />
            </div>
          </Flex>

          {warning ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '16px',
                borderRadius: '4px',
                backgroundColor: '#f4c3cb',
              }}
            >
              <Icon icon={Alert} size={16} style={{ flex: '0 0 auto', transform: 'translate3d(0,2px,0)' }} />
              <Text type="text3" align="start" ellipsis={false}>
                {warning}
              </Text>
            </div>
          ) : null}

          <Flex direction="column" align="start" gap={6}>
            <Text type="text2">Function</Text>
            <div style={{ position: 'relative', width: '100%' }}>
              <Dropdown
                size="medium"
                placeholder="Choose"
                clearable={false}
                searchable={false}
                value={dropdownValue(selectedFunctionOption)}
                options={FUNCTION_OPTIONS}
                onChange={(option: any) => {
                  if (option) {
                    setDraft(
                      option.value === 'count_items'
                        ? { ...draft, function: option.value, columnId: undefined }
                        : { ...draft, function: option.value },
                    );
                  }
                }}
              />
            </div>
          </Flex>

          {!isCountItems ? (
            <Flex direction="column" align="start" gap={6}>
              <Text type="text2">Numbers column</Text>
              <ColumnPicker
                columns={numbersColumns}
                value={draft.columnId}
                isDisabled={!source || isLoading}
                placeholder={numbersColumns.length || !board ? 'Choose column' : 'This board has no numbers columns'}
                hideIcons={true}
                onChange={(column) => setDraft({ ...draft, columnId: column?.id })}
              />
            </Flex>
          ) : null}
        </Flex>
      </AppModalContent>

      <AppModalFooter>
        <Flex gap={8} justify={'end' as any}>
          <Button kind="secondary" size="medium" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" size="medium" disabled={!isComplete} onClick={() => onSave(draft)}>
            Save
          </Button>
        </Flex>
      </AppModalFooter>
    </AppModal>
  );
}

type SortableKeyFigureRowProps = {
  id: string;
  figure: KeyFigure;
  index: number;
  isOnly: boolean;
  sources: KeyFigureSource[];
  portalSettings: PortalSettings;
  clientBoardSubitemsBoardId: number | null;
  onUpdate: (index: number, updates: Partial<KeyFigure>) => void;
  onDelete: (index: number) => void;
  onOpenSourceModal: (index: number) => void;
  onOpenPagePicker: (index: number) => void;
};

function SortableKeyFigureRow({
  id,
  figure,
  index,
  isOnly,
  sources,
  portalSettings,
  clientBoardSubitemsBoardId,
  onUpdate,
  onDelete,
  onOpenSourceModal,
  onOpenPagePicker,
}: SortableKeyFigureRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isOnly,
  });
  const { source, column, warning } = useKeyFigureSource(figure, sources, portalSettings, clientBoardSubitemsBoardId);
  const hasExternalUrl = isExternalUrl(figure.url);
  const isCountItems = figure.function === 'count_items';

  // Keep the full setup in the tooltip, with just the column name on the button.
  const functionLabel = FUNCTION_OPTIONS.find((option) => option.value === figure.function)?.label ?? '';
  const sourceSummary =
    hasKeyFigureSource(figure) && source
      ? [functionLabel, !isCountItems && column?.title, source.pageName].filter(Boolean).join(' · ')
      : null;
  const sourceButtonLabel = sourceSummary ? (isCountItems ? 'Count items' : column?.title) : null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1000 : undefined,
    position: isDragging ? ('relative' as const) : undefined,
    width: '100%',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Flex gap={8} direction="column" align="stretch" style={{ width: '100%' }}>
        <div style={ROW_GRID_STYLE}>
          <div>
            <div
              className="custom__teaser__draghandle"
              {...attributes}
              {...listeners}
              style={{
                cursor: isOnly ? 'default' : isDragging ? 'grabbing' : 'grab',
                opacity: isOnly ? 0.4 : 1,
              }}
            >
              <Icon icon={Drag} size={18} />
            </div>
          </div>

          <div>
            <Dropdown
              size="medium"
              clearable={false}
              searchable={false}
              menuWrapperClassName="custom__keyfigure__iconmenu"
              value={dropdownValue(ICON_OPTIONS.find((option) => option.value === (figure.icon || '')) ?? null)}
              options={ICON_OPTIONS}
              optionRenderer={renderIconOption}
              valueRenderer={renderIconValue}
              onChange={(option: any) => {
                onUpdate(index, { icon: option?.value || undefined });
              }}
            />
          </div>

          <div>
            <TextField
              size="medium"
              value={figure.label || ''}
              onChange={(newValue) => {
                onUpdate(index, { label: getTextFieldValue(newValue) || '' });
              }}
            />
          </div>

          <div style={{ minWidth: 0 }} title={sourceSummary ?? source?.label}>
            <Button
              type="button"
              kind={sourceSummary ? 'secondary' : 'primary'}
              size="medium"
              color={warning ? 'negative' : 'primary'}
              leftIcon={warning ? Alert : Settings}
              className="custom__keyfigure__sourcebutton"
              onClick={() => onOpenSourceModal(index)}
            >
              <span>{sourceButtonLabel ?? 'Set up the number'}</span>
            </Button>
          </div>

          <div style={{ minWidth: 0 }}>
            <TextField
              size="medium"
              value={figure.url || ''}
              placeholder="https:// or /page/alias"
              onChange={(newValue) => {
                onUpdate(index, { url: getTextFieldValue(newValue) || '' });
              }}
              icon={hasExternalUrl ? CloseSmall : Link}
              clearOnIconClick={hasExternalUrl}
              onIconClick={
                hasExternalUrl
                  ? undefined
                  : () => {
                      onOpenPagePicker(index);
                    }
              }
            />
          </div>

          <div>
            <SwatchSetting
              color={figure.backgroundColor}
              onChange={(newColor) => {
                onUpdate(index, { backgroundColor: newColor });
              }}
            />
          </div>

          <div>
            <IconButton
              icon={Delete}
              kind="tertiary"
              size="medium"
              disabled={isOnly}
              onClick={() => {
                onDelete(index);
              }}
              className="custom__deletepagebutton"
            />
          </div>
        </div>
      </Flex>
    </div>
  );
}

type KeyFiguresFieldProps = {
  portalSettings: PortalSettings;
  value: KeyFiguresFieldValue | undefined;
  fieldDescriptor: KeyFiguresFieldDescriptor;
  onChange: (value: KeyFiguresFieldValue | undefined) => void;
  clientBoardSubitemsBoardId: number | null;
};

export function KeyFiguresField({ portalSettings, value, onChange, clientBoardSubitemsBoardId }: KeyFiguresFieldProps) {
  const sources = useMemo(() => getKeyFigureSources(portalSettings), [portalSettings]);
  const [sourceModalIndex, setSourceModalIndex] = useState<number | null>(null);
  const [pagePickerIndex, setPagePickerIndex] = useState<number | null>(null);
  const [rowIds, setRowIds] = useState<string[]>(() => (value ?? []).map(() => makeRowId()));

  const figures = value ?? [];
  const selectedUrl = pagePickerIndex != null ? figures[pagePickerIndex]?.url : undefined;
  const selectedPageId = selectedUrl
    ? portalSettings.pages.find((page) => `/page/${page.id}` === selectedUrl.split('#')[0])?.id
    : undefined;

  // the id ties the computed value to its card; a figure from the section's default value gets one on its first edit
  const updateFigure = (index: number, updates: Partial<KeyFigure>) => {
    const newFigures = [...figures];
    const current = newFigures[index]!;
    newFigures[index] = { ...current, id: current.id || createRandomId(), ...updates };
    onChange(newFigures);
  };

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const sortableItems = useMemo(
    () => (rowIds.length === figures.length ? rowIds : figures.map(() => makeRowId())),
    [rowIds, figures.length],
  );

  // a new figure has no number until its source is set up, so that comes first
  const handleAdd = () => {
    setRowIds([...sortableItems, makeRowId()]);
    onChange([...figures, { id: createRandomId(), function: 'sum' }]);
  };

  const handleDelete = (index: number) => {
    setRowIds(sortableItems.filter((_, i) => i !== index));
    onChange(figures.filter((_, i) => i !== index));
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sortableItems.findIndex((id) => id === active.id);
    const newIndex = sortableItems.findIndex((id) => id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    setRowIds(arrayMove(sortableItems, oldIndex, newIndex));
    onChange(arrayMove(figures, oldIndex, newIndex));
  }

  return (
    <Box
      className="section__field section__field--key-figures"
      style={{ overflow: 'visible', ...getSwatchThemeStyle(portalSettings.theme) }}
    >
      {sourceModalIndex != null && figures[sourceModalIndex] ? (
        <KeyFigureSourceModal
          figure={figures[sourceModalIndex]!}
          sources={sources}
          portalSettings={portalSettings}
          clientBoardSubitemsBoardId={clientBoardSubitemsBoardId}
          onClose={() => setSourceModalIndex(null)}
          onSave={(setup) => {
            updateFigure(sourceModalIndex, setup);
            setSourceModalIndex(null);
          }}
        />
      ) : null}

      {pagePickerIndex != null ? (
        <PagePickerModal
          title="Link to portal page"
          icon={Link}
          description="Choose the page this key figure card links to in the client portal."
          selectedPageId={selectedPageId}
          portalSettings={portalSettings}
          isOpen
          onClose={() => {
            setPagePickerIndex(null);
          }}
          onSelect={(page) => {
            if (pagePickerIndex == null) return;

            updateFigure(pagePickerIndex, { url: `/page/${page.id}` });
            setPagePickerIndex(null);
          }}
        />
      ) : null}

      <Box style={{ marginTop: '-6px' }}>
        <Flex gap={14} direction="column" style={{ width: '100%' }}>
          {figures.length > 0 ? (
            <>
              <Box style={{ width: '100%' }}>
                <div style={{ ...ROW_GRID_STYLE, pointerEvents: 'none' }}>
                  <div>
                    <Text type="text2" color="primary">
                      &nbsp;
                    </Text>
                  </div>

                  <div>
                    <Text type="text2" color="primary">
                      Icon
                    </Text>
                  </div>

                  <div>
                    <Text type="text2" color="primary">
                      Label
                    </Text>
                  </div>

                  <div>
                    <Text type="text2" color="primary">
                      Number
                    </Text>
                  </div>

                  <div>
                    <Text type="text2" color="primary">
                      URL
                    </Text>
                  </div>

                  <div>
                    <Text type="text2" color="primary">
                      Color
                    </Text>
                  </div>
                </div>
              </Box>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                  <Flex gap={14} direction="column" style={{ width: '100%' }}>
                    {figures.map((figure, index) => {
                      const id = sortableItems[index];
                      if (!id) return null;

                      return (
                        <SortableKeyFigureRow
                          key={id}
                          id={id}
                          figure={figure}
                          index={index}
                          isOnly={figures.length === 1}
                          sources={sources}
                          portalSettings={portalSettings}
                          clientBoardSubitemsBoardId={clientBoardSubitemsBoardId}
                          onUpdate={updateFigure}
                          onDelete={handleDelete}
                          onOpenSourceModal={setSourceModalIndex}
                          onOpenPagePicker={setPagePickerIndex}
                        />
                      );
                    })}
                  </Flex>
                </SortableContext>
              </DndContext>

              <Box style={{ width: '100%', textAlign: 'right' }}>
                <IconButton icon={AddSmall} kind="primary" size="medium" onClick={handleAdd} />
              </Box>
            </>
          ) : (
            <Box style={{ width: '100%', textAlign: 'right' }}>
              <IconButton icon={AddSmall} kind="primary" size="medium" onClick={handleAdd} />
            </Box>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
